import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaStar } from 'react-icons/fa';

const reviews = [
  {
    id: 1,
    name: "Sarah Johnson",
    role: "Homeowner",
    location: "Fort Worth, TX",
    project: "Pergola & Outdoor Kitchen",
    text: "Xtreme Roofing transformed our backyard with a stunning pergola and outdoor kitchen. Their attention to detail and craftsmanship was exceptional. The team was professional, and the project was completed ahead of schedule. We're absolutely loving our new outdoor living space!",
    rating: 5
  },
  {
    id: 2,
    name: "Michael Rodriguez",
    role: "Property Owner",
    location: "Arlington, TX",
    project: "Retaining Wall",
    text: "After getting multiple quotes for our retaining wall project, Xtreme stood out with their expertise and competitive pricing. They handled all permits, built a beautiful wall that solved our erosion issues, and kept us informed throughout the process. Highly recommend!",
    rating: 5
  },
  {
    id: 3,
    name: "Emily Thompson",
    role: "Homeowner",
    location: "Keller, TX",
    project: "Concrete Patio",
    text: "Our concrete patio project was a game-changer! The Xtreme team helped design a perfect space for entertaining, complete with a custom stain that matches our home perfectly. Their concrete work is flawless and the customer service was outstanding.",
    rating: 5
  },
  {
    id: 4,
    name: "David Martinez",
    role: "Business Owner",
    location: "Southlake, TX",
    project: "Commercial Kitchen",
    text: "We hired Xtreme for a commercial outdoor kitchen installation at our restaurant. Their team was incredibly professional, worked around our business hours, and delivered a beautiful result that our customers love. The quality of work exceeded our expectations.",
    rating: 5
  },
  {
    id: 5,
    name: "Jennifer Wilson",
    role: "Homeowner",
    location: "Grapevine, TX",
    project: "Custom Pergola",
    text: "From design to completion, our pergola project was handled with utmost professionalism. The team at Xtreme helped us create the perfect outdoor living space, suggesting features we hadn't even considered. The craftsmanship is outstanding and the space is exactly what we wanted.",
    rating: 5
  },
  {
    id: 6,
    name: "Robert Anderson",
    role: "Property Developer",
    location: "North Richland Hills, TX",
    project: "Multi-Project Development",
    text: "Working with Xtreme on multiple projects has been fantastic. Their expertise in outdoor construction is unmatched. Whether it's retaining walls, concrete work, or outdoor kitchens, they consistently deliver quality results on time and within budget.",
    rating: 5
  }
];

const ReviewsSection = () => {
  const [activeReview, setActiveReview] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveReview((prev) => (prev + 1) % reviews.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Container>
      <Inner>
        <HeaderSection>
          <HeaderText>
            What Our <GradientText>Clients</GradientText> Say
          </HeaderText>
          <HeaderSubtext>
            Real feedback from our valued customers
          </HeaderSubtext>
        </HeaderSection>
        
        <ReviewDisplay>
          <AnimatePresence mode="wait">
            <Motion
              key={activeReview}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <ReviewCard>
                <ProjectTag>{reviews[activeReview].project}</ProjectTag>
                <QuoteIcon>"</QuoteIcon>
                <ReviewText>{reviews[activeReview].text}</ReviewText>
                <ReviewerInfo>
                  <ReviewerDetails>
                    <ReviewerName>{reviews[activeReview].name}</ReviewerName>
                    <ReviewerMeta>
                      <ReviewerRole>{reviews[activeReview].role}</ReviewerRole>
                      <LocationDot>•</LocationDot>
                      <ReviewerLocation>{reviews[activeReview].location}</ReviewerLocation>
                    </ReviewerMeta>
                  </ReviewerDetails>
                  <RatingStars>
                    {[...Array(5)].map((_, i) => (
                      <StarIcon key={i} active={i < reviews[activeReview].rating} />
                    ))}
                  </RatingStars>
                </ReviewerInfo>
              </ReviewCard>
            </Motion>
          </AnimatePresence>
        </ReviewDisplay>

        <ControlsSection>
          <ProgressBar>
            <Progress 
              initial={{ scaleX: 0 }} 
              animate={{ scaleX: 1 }} 
              transition={{ duration: 5, repeat: Infinity, ease: "linear" }} 
            />
          </ProgressBar>

          <Indicators>
            {reviews.map((_, index) => (
              <Indicator 
                key={index}
                $active={index === activeReview}
                onClick={() => setActiveReview(index)}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </Indicators>
        </ControlsSection>
      </Inner>
    </Container>
  );
};

const Container = styled.section`
  background: linear-gradient(180deg, #1A1A1A 0%, #2C2C2C 100%);
  padding: 6rem 2rem;
  width: 100%;
  overflow: hidden;
  min-height: 100vh;
  display: flex;
  align-items: center;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const HeaderSection = styled.div`
  margin-bottom: 4rem;
  text-align: center;
`;

const HeaderText = styled.h2`
  font-size: 3.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeaderSubtext = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.2rem;
  font-weight: 400;
`;

const ReviewDisplay = styled.div`
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin: 2rem 0;
`;

const ReviewCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 3rem;
  border-radius: 24px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 107, 0, 0.5),
      transparent
    );
  }
`;

const QuoteIcon = styled.div`
  font-size: 6rem;
  color: rgba(255, 107, 0, 0.1);
  font-family: Georgia, serif;
  position: absolute;
  top: -1rem;
  left: 2rem;
`;

const ProjectTag = styled.div`
  background: linear-gradient(135deg, #FF6B00 0%, #FF8A00 100%);
  color: white;
  padding: 0.6rem 1.2rem;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  display: inline-block;
  margin-bottom: 2rem;
  box-shadow: 0 4px 12px rgba(255, 107, 0, 0.2);
`;

const ReviewText = styled.p`
  font-size: 1.5rem;
  font-weight: 500;
  color: #FFFFFF;
  margin-bottom: 2rem;
  line-height: 1.6;
  font-style: italic;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ReviewerInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1.5rem;
  margin-top: 2rem;
`;

const ReviewerDetails = styled.div`
  text-align: left;
`;

const ReviewerName = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
`;

const ReviewerMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.3rem;
  color: rgba(255, 255, 255, 0.7);
`;

const ReviewerRole = styled.span`
  color: #FF6B00;
`;

const LocationDot = styled.span`
  color: rgba(255, 255, 255, 0.3);
`;

const ReviewerLocation = styled.span`
  color: rgba(255, 255, 255, 0.7);
`;

const RatingStars = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const StarIcon = styled(FaStar)`
  color: ${props => props.active ? '#FF6B00' : 'rgba(255, 107, 0, 0.2)'};
  font-size: 1.2rem;
`;

const Motion = styled(motion.div)`
  width: 100%;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 2px;
  background: rgba(255, 107, 0, 0.1);
  margin: 3rem 0;
  position: relative;
  overflow: hidden;
`;

const Progress = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #FF6B00;
  transform-origin: left;
`;

const Indicators = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
`;

const Indicator = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active ? '#FF6B00' : 'rgba(255, 107, 0, 0.1)'};
  cursor: pointer;
  padding: 0;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.$active ? '#FF8A00' : 'rgba(255, 107, 0, 0.2)'};
  }
`;

const ControlsSection = styled.div`
  margin-top: 3rem;
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #FF6B00 0%, #FF8A00 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

export default ReviewsSection;
